import { select, call, takeLatest, put } from 'redux-saga/effects';
import { toast } from 'react-toastify';
import { InvoiceActions } from '../constants';
import { InvoiceApi } from 'src/services/end-points';
import { StoreState } from '../reducers';
import { PaginationService } from '../../services/api-handlers/pagination';
import {
    DeleteInvoiceAction,
    ApproveInvoiceDocsAction,
    GetInvoiceAction,
    GetInvoicesAction,
    PersistInvoiceAction,
    NotifySignerOnInvoiceAction,
    CreateFilesInvoiceAction,
    GetFilesAction,
    GetSignersAction,
    CreateInvoiceAction,
    SignInvoiceDocAction,
    DeleteFileInvoiceAction,
    DeleteInvoiceDocsAction,
    GetInvoicesDocsAction,
    SendInvoiceToDocusignAction,
    SendInvoiceToParentAction,
    SendStatementToParentAction,
    GetInvoicesSmallAction,
    SaveInvoiceReasonAction,
    SaveInvoiceNoteAction,
} from '../actions/invoice-actions';
import sortingService from '../../services/sorting';
import {
    failureType,
    handleError,
    resolveApiCall,
    ResolverApiFailure,
    ResolverApiSuccess,
    successType,
} from '../../services/api-handlers/api-resolver';
import { SubmissionError } from 'redux-form';
import { makeFormErrors } from '../../components/redux-form/utils/make-form-errors';
import { convertValuesToFormData } from '../../components/redux-form/utils/values-to-form-data';
import { push } from 'connected-react-router';
import Router from '../../navigation/router';
import routes from '../../navigation/routes';
import moment from 'moment';
import { changeToZeroMoment } from '../../helpers/timeHelpers';
import { groupInvoiceToCsv } from '../../helpers/invoice/groupCsv';
import { exportToCsv } from 'src/services/exports/csv';
import { GetExpensesAction } from '../actions/expense-actions';
import { groupInvoiceTotal } from '../../helpers/groupTotalInvoice';

function* handleFetchInvoices(action: GetInvoicesAction) {
    const oldData = yield select((state: StoreState) => state.invoices.data);
    const { filters, sorting, page, itemsPerPage, notTotal } = action.payload;

    let filterProps: any = { ...filters };
    if (filters?.createdAt) {
        const createdAtDate = moment(filters.createdAt).format('YYYY-MM-DD');
        filterProps = {
            ...filters,
            'createdAt[before]': createdAtDate + ' 23:59:59',
            'createdAt[after]': createdAtDate,
        };
        delete filterProps.createdAt;
    }

    //periodStart[after]: "2021-04-20"
    //periodStart[before]: "2021-04-22"
    if (filterProps?.periodStart) {
        const periodStart = changeToZeroMoment(`${filterProps?.periodStart['periodStart[after]']}`, 'YYYY-MM-DD');
        const periodEnd = changeToZeroMoment(`${filterProps.periodStart['periodStart[before]']}`, 'YYYY-MM-DD');
        filterProps = {
            ...filterProps,
            'periodStart[after]': periodStart,
            'periodStart[before]': periodEnd,
        };
        delete filterProps.periodStart;
    }

    if (!notTotal) {
        const actionFiles: GetInvoicesSmallAction = {
            type: InvoiceActions.FETCH_INVOICES_TOTALS,
            payload: {
                filters: filterProps,
                page: 1,
            },
        };
        yield call(handleFetchInvoicesTotal, actionFiles);
    }

    yield PaginationService.setPageSize(itemsPerPage).resolveApiCall(action, page, oldData, () =>
        InvoiceApi.getInvoices({
            filters: filterProps,
            ...filterProps,
            order: sortingService.makeOrder(sorting),
            page,
            itemsPerPage,
            pagination: itemsPerPage === -1 ? 0 : 1,
        }),
    );
}

function* handleFetchInvoicesSmall(action: GetInvoicesSmallAction) {
    const oldData = yield select((state: StoreState) => state.invoices.data);
    const { filters, sorting, page, itemsPerPage, notTotal } = action.payload;

    let filterProps: any = { ...filters };
    if (filters?.createdAt) {
        const createdAtDate = moment(filters.createdAt).format('YYYY-MM-DD');
        filterProps = {
            ...filters,
            'createdAt[before]': createdAtDate + ' 23:59:59',
            'createdAt[after]': createdAtDate,
        };
        delete filterProps.createdAt;
    }

    //periodStart[after]: "2021-04-20"
    //periodStart[before]: "2021-04-22"
    if (filterProps?.periodStart) {
        const periodStart = changeToZeroMoment(`${filterProps?.periodStart['periodStart[after]']}`, 'YYYY-MM-DD');
        const periodEnd = changeToZeroMoment(`${filterProps.periodStart['periodStart[before]']}`, 'YYYY-MM-DD');
        filterProps = {
            ...filterProps,
            'periodStart[after]': periodStart,
            'periodStart[before]': periodEnd,
        };
        delete filterProps.periodStart;
    }

    if (!notTotal) {
        const actionFiles: GetInvoicesSmallAction = {
            type: InvoiceActions.FETCH_INVOICES_TOTALS,
            payload: {
                filters: filterProps,
                page: 1,
            },
        };
        yield call(handleFetchInvoicesTotal, actionFiles);
    }

    yield PaginationService.setPageSize(itemsPerPage).resolveApiCall(action, page, oldData, () =>
        InvoiceApi.getInvoicesSmall({
            filters: filterProps,
            ...filterProps,
            order: sortingService.makeOrder(sorting),
            page,
            itemsPerPage,
            pagination: itemsPerPage === -1 ? 0 : 1,
        }),
    );
}

function* handleFetchInvoicesTotal(action: GetInvoicesSmallAction): any {
    const { filters, sorting, itemsPerPage } = action.payload;
    let page: number = action?.payload?.page ? +action.payload.page : 1;

    let filterProps: any = { ...filters };
    if (filters?.createdAt) {
        filterProps = {
            ...filters,
            'createdAt[before]': moment(filters.createdAt).format('YYYY-MM-DD') + ' 23:59:59',
            'createdAt[after]': moment(filters.createdAt).format('YYYY-MM-DD'),
        };
        delete filterProps.createdAt;
    }

    //periodStart[after]: "2021-04-20"
    //periodStart[before]: "2021-04-22"
    if (filterProps?.periodStart) {
        const periodStart = changeToZeroMoment(`${filterProps?.periodStart['periodStart[after]']}`, 'YYYY-MM-DD');
        const periodEnd = changeToZeroMoment(`${filterProps.periodStart['periodStart[before]']}`, 'YYYY-MM-DD');
        filterProps = {
            ...filterProps,
            'periodStart[after]': periodStart,
            'periodStart[before]': periodEnd,
        };
        delete filterProps.periodStart;
    }

    yield resolveApiCall(
        action,
        () =>
            InvoiceApi.getInvoicesSmall({
                filters: filterProps,
                ...filterProps,
                order: sortingService.makeOrder(sorting),
                page,
                itemsPerPage: 30,
                pagination: itemsPerPage === -1 ? 0 : 1,
            }),
        function* (data) {
            if (data.payload.length === 30) {
                const actionFiles: GetInvoicesSmallAction = {
                    type: InvoiceActions.FETCH_INVOICES_TOTALS,
                    payload: {
                        filters: filterProps,
                        ...filterProps,
                        page: ++page,
                        sorting,
                        itemsPerPage: 30,
                    },
                    all: action.all ? [...action.all, ...data.payload] : [...data.payload],
                };

                yield call(handleFetchInvoicesTotal, actionFiles);
            } else {
                if (action.all) {
                    let groupData;
                    if (data.payload) {
                        groupData = groupInvoiceTotal([...action.all, ...data.payload]);
                    } else {
                        groupData = groupInvoiceTotal(action.all);
                    }
                    yield put({
                        type: successType(action.type),
                        payload: groupData,
                        total: true,
                    });
                } else {
                    const groupData = groupInvoiceTotal(data.payload);
                    yield put({
                        type: successType(action.type),
                        payload: groupData,
                        total: true,
                    });
                }
            }
        },
        function (result: ResolverApiFailure) {
            if (result.error && result.error.response && result.error.response.status) {
                toast.error(result.error.response.data.detail ?? result.error.message ?? 'Unknown error');
            }
            handleError(result);
        },
    );
}

function* handleFetchInvoicesDocs(action: GetInvoicesDocsAction) {
    const oldData = yield select((state: StoreState) => state.invoices.dataDocs);
    const { filters, sorting, page, itemsPerPage } = action.payload;

    const filterProps: any = { ...filters };

    yield PaginationService.setPageSize(itemsPerPage).resolveApiCall(action, page, oldData, () =>
        InvoiceApi.getInvoicesDocs({
            ...filterProps,
            order: sortingService.makeOrder(sorting),
            page,
            itemsPerPage,
            pagination: itemsPerPage === -1 ? 0 : 1,
        }),
    );
}

function* handleDeclineInvoicesDocs(action: DeleteInvoiceDocsAction) {
    const {
        payload: { id, data, onSuccess, onError },
    } = action;

    yield resolveApiCall(
        action,
        () => InvoiceApi.declineInvoiceDoc(id, data),
        function () {
            if (onSuccess) {
                onSuccess();
            }
        },
        function (result: ResolverApiFailure) {
            if (result.error && result.error.response && result.error.response.status) {
                if (onError) {
                    onError(result.error.response.data.detail ?? result.error.message ?? 'Unknown error');
                }
            }
            handleError(result);
        },
    );
    const actionFiles: GetInvoicesDocsAction = {
        type: InvoiceActions.FETCH_INVOICE_DOCS,
        payload: {
            page: 1,
            itemsPerPage: 30,
            filters: {
                isApproved: false,
            },
        },
    };

    yield call(handleFetchInvoicesDocs, actionFiles);
}

function* handleApproveInvoicesDocs(action: ApproveInvoiceDocsAction) {
    const {
        payload: { id, data, onSuccess, onError },
    } = action;

    yield resolveApiCall(
        action,
        () => InvoiceApi.approveInvoiceDoc(id, data),
        function () {
            if (onSuccess) {
                onSuccess();
            }
        },
        function (result: ResolverApiFailure) {
            if (result.error && result.error.response && result.error.response.status) {
                if (onError) {
                    onError(result.error.response.data.detail ?? result.error.message ?? 'Unknown error');
                }
            }
            handleError(result);
        },
    );
    const actionFiles: GetInvoicesDocsAction = {
        type: InvoiceActions.FETCH_INVOICE_DOCS,
        payload: {
            page: 1,
            itemsPerPage: 30,
            filters: {
                isApproved: false,
            },
        },
    };

    yield call(handleFetchInvoicesDocs, actionFiles);
}

function* handleFetchInvoicesCsv(action: GetInvoicesAction): any {
    const { filters, sorting, itemsPerPage } = action.payload;
    let page: number = action?.payload?.page ? +action.payload.page : 1;
    let filterProps: any = { ...filters };
    if (filters?.createdAt) {
        filterProps = {
            ...filters,
            'createdAt[before]': moment(filters.createdAt).format('YYYY-MM-DD') + ' 23:59:59',
            'createdAt[after]': moment(filters.createdAt).format('YYYY-MM-DD'),
        };
        delete filterProps.createdAt;
    }
    if (filterProps?.periodStart) {
        const periodStart = changeToZeroMoment(`${filterProps?.periodStart['periodStart[after]']}`, 'YYYY-MM-DD');
        const periodEnd = changeToZeroMoment(`${filterProps.periodStart['periodStart[before]']}`, 'YYYY-MM-DD');

        filterProps = {
            ...filterProps,
            'periodStart[after]': periodStart,
            'periodStart[before]': periodEnd,
        };
        delete filterProps.periodStart;
    }

    yield resolveApiCall(
        action,
        () =>
            InvoiceApi.getInvoices({
                ...filterProps,
                order: sortingService.makeOrder(sorting),
                page,
                itemsPerPage: 60,
                pagination: itemsPerPage === -1 ? 0 : 1,
            }),
        function* (data) {
            if (data.payload.length === 60) {
                const actionFiles: GetExpensesAction = {
                    type: InvoiceActions.FETCH_INVOICES_CSV,
                    payload: {
                        filters: filterProps,
                        ...filterProps,
                        page: ++page,
                        sorting,
                        itemsPerPage: 60,
                    },
                    all: action.all ? [...action.all, ...data.payload] : [...data.payload],
                };

                yield call(handleFetchInvoicesCsv, actionFiles);
            } else {
                const dataAll = action.all ? [...action.all, ...data.payload] : [...data.payload];
                if (action.all) {
                    const groupCsv = groupInvoiceToCsv(dataAll);
                    exportToCsv('invoices.csv', groupCsv);
                } else {
                    const groupCsvData = groupInvoiceToCsv(data.payload);
                    const actionResult = {
                        payload: {},
                        type: successType(InvoiceActions.FETCH_INVOICES_CSV),
                    };

                    yield put(actionResult);
                    exportToCsv('invoices.csv', groupCsvData);
                }
            }
        },
        function* (result: ResolverApiFailure) {
            if (result.error && result.error.response && result.error.response.status) {
                toast.error(result.error.response.data.detail ?? result.error.message ?? 'Unknown error');
                const actionResult = {
                    payload: {},
                    type: failureType(InvoiceActions.FETCH_INVOICES_CSV),
                };
                yield put(actionResult);
            }
            handleError(result);
        },
    );
}
function* handleFetchInvoice(action: GetInvoiceAction) {
    const {
        payload: { id },
    } = action;

    yield resolveApiCall(action, () => InvoiceApi.getInvoice(id));
}

function* handleSignInvoiceDoc(action: SignInvoiceDocAction) {
    const {
        payload: { id, files, onSuccess, onError },
    } = action;

    for (let i = 0; i < files.length; i++) {
        if (files[i]?.id || files[i]?.attach || !files[i].type) {
            continue;
        }

        const formData = convertValuesToFormData({ attachSignedFile: files[i] }, '');
        yield resolveApiCall(
            action,
            () => InvoiceApi.signInvoiceDoc({ formData, id }),
            function () {
                if (onSuccess) {
                    onSuccess();
                }
            },
            function (result: ResolverApiFailure) {
                if (result.error && result.error.response && result.error.response.status) {
                    if (onError) {
                        onError(result.error.response.data.detail ?? result.error.message ?? 'Unknown error');
                    }
                }
                handleError(result);
            },
        );
    }
}

function* handleCreateInvoice(action: CreateInvoiceAction) {
    const {
        payload: { data, onSuccess, onError },
    } = action;

    yield resolveApiCall(
        action,
        () => InvoiceApi.createInvoiceCase(data),
        function () {
            if (onSuccess) {
                onSuccess();
            }
        },
        function (result: ResolverApiFailure) {
            if (result.error && result.error.response && result.error.response.status) {
                if (onError) {
                    onError(result.error.response.data.detail ?? result.error.message ?? 'Unknown error');
                }
            }
            handleError(result);
        },
    );
}

function* handleDeleteInvoice(action: DeleteInvoiceAction) {
    const {
        payload: { id, onSuccess, onError },
    } = action;

    yield resolveApiCall(
        action,
        () => InvoiceApi.deleteInvoice(id),
        function () {
            if (onSuccess) {
                onSuccess();
            }
        },
        function (result: ResolverApiFailure) {
            if (result.error && result.error.response && result.error.response.status) {
                if (onError) {
                    onError(result.error.response.data.detail ?? result.error.message ?? 'Unknown error');
                }
            }
            handleError(result);
        },
    );
}

function* handleNotifySignerOnInvoice(action: NotifySignerOnInvoiceAction) {
    const { payload } = action;
    const {
        data: { id },
        callbacks,
    } = payload;

    yield resolveApiCall(
        action,
        () => InvoiceApi.notifySignerOnInvoice(id),
        function* (data: ResolverApiSuccess) {
            yield call(callbacks.resolve);
            if (!id) {
                yield put(push(Router.generate(routes.INVOICES_LIST)));
            }
        },
        function* (result: ResolverApiFailure) {
            if (result.error && result.error.response && result.error.response.status) {
                yield call(
                    callbacks.reject,
                    result.error instanceof SubmissionError
                        ? result.error
                        : new SubmissionError({
                              _error: result.error.message ?? 'Incorrect response',
                              ...makeFormErrors(result.error.response?.data),
                          }),
                );
            }
            handleError(result);
        },
    );
}

function* handleSendInvoiceToDocusign(action: SendInvoiceToDocusignAction) {
    const { payload } = action;
    const {
        data: { id, ...restData },
        callbacks,
    } = payload;

    yield resolveApiCall(
        action,
        () => InvoiceApi.sentToDocusign(id, restData),
        function* (data: ResolverApiSuccess) {
            yield call(callbacks.resolve);

            const actionInvoiceFetch: GetInvoiceAction = {
                type: InvoiceActions.FETCH_INVOICE,
                payload: {
                    id: id,
                },
            };
            yield call(handleFetchInvoice, actionInvoiceFetch);

            const actionSignersFetch: GetSignersAction = {
                type: InvoiceActions.FETCH_SIGNERS_INVOICE,
                payload: {
                    id: id,
                },
            };
            yield call(handleGetSignersInvoice, actionSignersFetch);

            if (restData.RequestSent) {
                const actionFilesFetch: GetInvoicesAction = {
                    type: InvoiceActions.FETCH_INVOICES,
                    payload: {
                        page: 1,
                        itemsPerPage: 20,
                        filters: {
                            ...restData.filters,
                        },
                    },
                };
                yield call(handleFetchInvoices, actionFilesFetch);
            }

            if (!id) {
                yield put(push(Router.generate(routes.INVOICES_LIST)));
            }
        },
        function* (result: ResolverApiFailure) {
            if (result.error && result.error.response && result.error.response.status) {
                toast.error(result.error.response.data.detail ?? result.error.message ?? 'Unknown error');
            }
            handleError(result);
        },
    );
}

function* handleSendInvoiceToParent(action: SendInvoiceToParentAction) {
    const { payload } = action;
    const {
        data: { id, ...restData },
        callbacks,
    } = payload;

    yield resolveApiCall(
        action,
        () => InvoiceApi.sentToParent(id),
        function* (data: ResolverApiSuccess) {
            yield call(callbacks.resolve);

            const actionInvoiceFetch: GetInvoiceAction = {
                type: InvoiceActions.FETCH_INVOICE,
                payload: {
                    id: id,
                },
            };
            yield call(handleFetchInvoice, actionInvoiceFetch);

            const actionSignersFetch: GetSignersAction = {
                type: InvoiceActions.FETCH_SIGNERS_INVOICE,
                payload: {
                    id: id,
                },
            };
            yield call(handleGetSignersInvoice, actionSignersFetch);

            if (restData.RequestSent) {
                const actionFilesFetch: GetInvoicesAction = {
                    type: InvoiceActions.FETCH_INVOICES,
                    payload: {
                        page: 1,
                        itemsPerPage: 20,
                        filters: {
                            ...restData.filters,
                        },
                    },
                };
                yield call(handleFetchInvoices, actionFilesFetch);
            }

            if (!id) {
                yield put(push(Router.generate(routes.INVOICES_LIST)));
            }
        },
        function* (result: ResolverApiFailure) {
            if (result.error && result.error.response && result.error.response.status) {
                toast.error(result.error.response.data.detail ?? result.error.message ?? 'Unknown error');
            }
            handleError(result);
        },
    );
}

function* handleSaveInvoiceReason(action: SaveInvoiceReasonAction) {
    const { payload } = action;
    const {
        data: { id, reason, status, ...restData },
        callbacks,
    } = payload;

    yield resolveApiCall(
        action,
        () => InvoiceApi.saveInvoiceReason(id, reason ? reason : '', status ? status : 10),
        function* (data: ResolverApiSuccess) {
            yield call(callbacks.resolve);

            const actionInvoiceFetch: GetInvoiceAction = {
                type: InvoiceActions.FETCH_INVOICE,
                payload: {
                    id: id,
                },
            };
            yield call(handleFetchInvoice, actionInvoiceFetch);

            if (!id) {
                yield put(push(Router.generate(routes.INVOICES_LIST)));
            }
        },
        function* (result: ResolverApiFailure) {
            if (result.error && result.error.response && result.error.response.status) {
                toast.error(result.error.response.data.detail ?? result.error.message ?? 'Unknown error');
            }
            handleError(result);
        },
    );
}

function* handleSaveInvoiceNote(action: SaveInvoiceNoteAction) {
    const { payload } = action;
    const {
        data: { id, note, status, ...restData },
        callbacks,
    } = payload;

    yield resolveApiCall(
        action,
        () => InvoiceApi.saveInvoiceNote(id, note ? note : ''),
        function* (data: ResolverApiSuccess) {
            yield call(callbacks.resolve);

            const actionInvoiceFetch: GetInvoiceAction = {
                type: InvoiceActions.FETCH_INVOICE,
                payload: {
                    id: id,
                },
            };
            yield call(handleFetchInvoice, actionInvoiceFetch);

            if (!id) {
                yield put(push(Router.generate(routes.INVOICES_LIST)));
            }
        },
        function* (result: ResolverApiFailure) {
            if (result.error && result.error.response && result.error.response.status) {
                toast.error(result.error.response.data.detail ?? result.error.message ?? 'Unknown error');
            }
            handleError(result);
        },
    );
}

function* handleSendStatementToParent(action: SendStatementToParentAction) {
    const { payload } = action;
    const {
        data: { id, ...restData },
        callbacks,
    } = payload;

    yield resolveApiCall(
        action,
        () => InvoiceApi.sentStatementToParent(id),
        function* (data: ResolverApiSuccess) {
            yield call(callbacks.resolve);

            if (!id) {
                yield put(push(Router.generate(routes.INVOICES_LIST)));
            }
        },
        function* (result: ResolverApiFailure) {
            if (result.error && result.error.response && result.error.response.status) {
                toast.error(result.error.response.data.detail ?? result.error.message ?? 'Unknown error');
            }
            handleError(result);
        },
    );
}

function* handlePersistInvoice(action: PersistInvoiceAction) {
    const { payload } = action;
    const {
        data: { id, ...restData },
        files,
        callbacks,
    } = payload;

    yield resolveApiCall(
        action,
        () => (id ? InvoiceApi.updateInvoice(id, restData) : InvoiceApi.createInvoice(restData)),
        function* (data: ResolverApiSuccess) {
            const actionFiles: CreateFilesInvoiceAction = {
                type: InvoiceActions.CREATE_FILES_INVOICE,
                payload: {
                    id: data.payload.id,
                    files,
                    callbacks,
                },
            };
            if (restData.RequestSent) {
                const actionFilesFetch: GetInvoicesAction = {
                    type: InvoiceActions.FETCH_INVOICES,
                    payload: {
                        page: 1,
                        itemsPerPage: 20,
                        filters: {
                            ...restData.filters,
                        },
                    },
                };
                yield call(handleFetchInvoices, actionFilesFetch);
            }
            yield call(handleCreateFilesInvoice, actionFiles);
            yield call(callbacks.resolve);
            if (!id) {
                yield put(push(Router.generate(routes.INVOICES_LIST)));
            }
        },
        function* (result: ResolverApiFailure) {
            if (result.error && result.error.response && result.error.response.status) {
                yield call(
                    callbacks.reject,
                    result.error instanceof SubmissionError
                        ? result.error
                        : new SubmissionError({
                              _error: result.error.message ?? 'Incorrect response',
                              ...makeFormErrors(result.error.response?.data),
                          }),
                );
            }
            handleError(result);
        },
    );
}

function* handleGetFilesInvoice(action: GetFilesAction) {
    const {
        payload: { id },
    } = action;

    yield resolveApiCall(action, () => InvoiceApi.getFilesInvoice(id));
}

function* handleGetSignersInvoice(action: GetSignersAction) {
    const {
        payload: { id },
    } = action;
    yield resolveApiCall(action, () => InvoiceApi.getSignersInvoice(id));
}

function* handleCreateFilesInvoice(action: CreateFilesInvoiceAction) {
    const {
        payload: { id, files, callbacks },
    } = action;

    for (let i = 0; i < files.length; i++) {
        if (files[i].id) {
            continue;
        }
        const formData = convertValuesToFormData({ user: id, attachFile: files[i] }, '');
        yield resolveApiCall(
            action,
            () => InvoiceApi.createFilesInvoice(formData),
            function* (data: ResolverApiSuccess) {
                yield call(callbacks.resolve);
            },
            function* (result: ResolverApiFailure) {
                if (result.error && result.error.response && result.error.response.status) {
                    yield call(
                        callbacks.reject,
                        result.error instanceof SubmissionError
                            ? result.error
                            : new SubmissionError({
                                  _error: result.error.message ?? 'Incorrect response',
                                  ...makeFormErrors(result.error.response?.data),
                              }),
                    );
                }
                handleError(result);
            },
        );
    }
}

function* handleDeleteFileInvoice(action: DeleteFileInvoiceAction) {
    const {
        payload: { id, onSuccess, onError },
    } = action;

    yield resolveApiCall(
        action,
        () => InvoiceApi.deleteFileInvoice(id),
        function (data) {
            if (onSuccess) {
                onSuccess();
            }
        },
        function (result: ResolverApiFailure) {
            if (result.error && result.error.response && result.error.response.status) {
                if (onError) {
                    onError(result.error.response.data.detail ?? result.error.message ?? 'Unknown error');
                }
            }
            handleError(result);
        },
    );
}

export default function* () {
    yield takeLatest(InvoiceActions.FETCH_INVOICES, handleFetchInvoices);
    yield takeLatest(InvoiceActions.FETCH_INVOICES_SIMPLY, handleFetchInvoicesSmall);
    yield takeLatest(InvoiceActions.FETCH_INVOICE_DOCS, handleFetchInvoicesDocs);
    yield takeLatest(InvoiceActions.DECLINE_INVOICE_DOCS, handleDeclineInvoicesDocs);
    yield takeLatest(InvoiceActions.APPROVE_INVOICE_DOCS, handleApproveInvoicesDocs);
    yield takeLatest(InvoiceActions.FETCH_INVOICES_CSV, handleFetchInvoicesCsv);
    yield takeLatest(InvoiceActions.FETCH_INVOICE, handleFetchInvoice);
    yield takeLatest(InvoiceActions.DELETE_INVOICE, handleDeleteInvoice);
    yield takeLatest(InvoiceActions.SIGN_INVOICE_DOC, handleSignInvoiceDoc);
    yield takeLatest(InvoiceActions.PERSIST_INVOICE, handlePersistInvoice);
    yield takeLatest(InvoiceActions.NOTIFY_SIGNER_ON_INVOICE, handleNotifySignerOnInvoice);
    yield takeLatest(InvoiceActions.SEND_INVOICE_TO_DOCUSIGN, handleSendInvoiceToDocusign);
    yield takeLatest(InvoiceActions.SEND_INVOICE_TO_PARENT, handleSendInvoiceToParent);
    yield takeLatest(InvoiceActions.SEND_STATEMENT_TO_PARENT, handleSendStatementToParent);
    yield takeLatest(InvoiceActions.DELETE_FILE_INVOICE, handleDeleteFileInvoice);
    yield takeLatest(InvoiceActions.CREATE_FILES_INVOICE, handleCreateFilesInvoice);
    yield takeLatest(InvoiceActions.FETCH_FILES_INVOICE, handleGetFilesInvoice);
    yield takeLatest(InvoiceActions.FETCH_SIGNERS_INVOICE, handleGetSignersInvoice);
    yield takeLatest(InvoiceActions.CREATE_INVOICE, handleCreateInvoice);
    yield takeLatest(InvoiceActions.FETCH_INVOICES_TOTALS, handleFetchInvoicesTotal);
    yield takeLatest(InvoiceActions.SAVE_INVOICE_REASON, handleSaveInvoiceReason);
    yield takeLatest(InvoiceActions.SAVE_INVOICE_NOTE, handleSaveInvoiceNote);
}
